/**
 * Deploy API client.
 *
 * Deploy jobs are created implicitly by agent mutations (POST/PATCH/DELETE
 * on /api/agents/...). The drawer polls `/api/deploy/{id}` every ~1s for
 * v1; WS streaming comes later via `deploy:<id>` topic on the event bus.
 */
import { api } from '@/api/client';

export type DeployStatus = 'pending' | 'running' | 'succeeded' | 'failed' | 'canceled';

export interface DeployStep {
  name: string;
  progress: number;
  status: 'pending' | 'running' | 'succeeded' | 'failed';
  started_at?: string;
  finished_at?: string;
}

export interface DeployJob {
  job_id: string;
  status: DeployStatus;
  started_at: string;
  finished_at?: string;
  duration_ms?: number;
  steps: DeployStep[];
  stdout: string;
  exit_code?: number;
  error?: string;
  agent?: string;
  kind: string;
}

export interface DeployHistoryEntry {
  job_id: string;
  status: DeployStatus;
  started_at: string;
  duration_ms?: number;
  kind: string;
  agent?: string;
}

export const deployApi = {
  async get(jobId: string): Promise<DeployJob> {
    return api<DeployJob>(`/deploy/${encodeURIComponent(jobId)}`);
  },

  async history(limit = 50): Promise<{ items: DeployHistoryEntry[] }> {
    return api<{ items: DeployHistoryEntry[] }>(`/deploy/history?limit=${limit}`);
  },

  async cancel(jobId: string, stepupToken: string): Promise<void> {
    await api<void>(`/deploy/${encodeURIComponent(jobId)}/cancel`, {
      method: 'POST',
      headers: { 'X-StepUp-Token': stepupToken },
    });
  },
};
